import { DateInput } from "@mantine/dates";
import type { UseFormReturnType } from "@mantine/form";
import { NumberInput, Switch, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { MAX_PACKAGES, type HawbDetailsValues } from "./hawbForm";

interface HawbDetailsFieldsProps<T extends HawbDetailsValues> {
  form: UseFormReturnType<T>;
  disabled?: boolean;
}

const HawbDetailsFields = <T extends HawbDetailsValues>({ form, disabled }: HawbDetailsFieldsProps<T>) => {
  const { t } = useTranslation();
  const errorOf = (field: keyof HawbDetailsValues) => {
    const error = form.errors[field];
    return typeof error === "string" ? t(error) : undefined;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <DateInput
        label={t("manifests.columns.arrival")}
        placeholder={t("manifests.form.arrivalPlaceholder")}
        valueFormat="YYYY-MM-DD"
        clearable
        disabled={disabled}
        {...form.getInputProps("arrival_date")}
      />
      <TextInput
        label={t("manifests.columns.origin")}
        placeholder={t("manifests.form.originPlaceholder")}
        disabled={disabled}
        {...form.getInputProps("origin_station")}
        error={errorOf("origin_station")}
      />
      <TextInput
        label={t("manifests.columns.destination")}
        placeholder={t("manifests.form.destinationPlaceholder")}
        disabled={disabled}
        {...form.getInputProps("destination")}
        error={errorOf("destination")}
      />
      <TextInput
        label={t("manifests.columns.responsiblePerson")}
        placeholder={t("manifests.form.responsiblePersonPlaceholder")}
        disabled={disabled}
        {...form.getInputProps("responsible_person")}
        error={errorOf("responsible_person")}
      />
      <NumberInput
        label={t("manifests.columns.packages")}
        required
        min={1}
        max={MAX_PACKAGES}
        allowDecimal={false}
        allowNegative={false}
        disabled={disabled}
        {...form.getInputProps("packages")}
        error={errorOf("packages")}
      />
      <NumberInput
        label={t("manifests.columns.weight")}
        min={0}
        decimalScale={3}
        allowNegative={false}
        disabled={disabled}
        {...form.getInputProps("weight_kg")}
        error={errorOf("weight_kg")}
      />
      <div className="md:col-span-2 flex flex-wrap gap-6 pt-2">
        <Switch
          label={t("manifests.columns.feePaid")}
          disabled={disabled}
          {...form.getInputProps("storage_fee_paid", { type: "checkbox" })}
        />
        <Switch
          label={t("manifests.columns.blocked")}
          disabled={disabled}
          {...form.getInputProps("blocked", { type: "checkbox" })}
        />
        <Switch
          label={t("manifests.columns.pickList")}
          disabled={disabled}
          {...form.getInputProps("has_pick_list", { type: "checkbox" })}
        />
      </div>
    </div>
  );
};

export default HawbDetailsFields;
